// components/TrackOrder.jsx
import React, { useState } from 'react';
// import "../styles/TrackOrder.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch,
    faTruck,
    faBox,
    faCalendarAlt,
    faMapMarkerAlt,
    faCheckCircle,
    faTimesCircle,
    faClock,
    faStore,
    faUser,
    faDollarSign,
    faPhone,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';

const TrackOrder = () => {
    const [trackingCode, setTrackingCode] = useState('');
    const [trackingInfo, setTrackingInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleTrack = async (e) => {
        e.preventDefault();
        
        if (!trackingCode.trim()) {
            setError('Please enter a tracking code');
            return;
        }

        setLoading(true);
        setError('');
        setTrackingInfo(null);

        try {
            const response = await fetch(`https://ourzestapp.online/oz_backend/tracking.php?tracking_code=${encodeURIComponent(trackingCode)}`);
            const data = await response.json();

            if (data.status === 'success') {
                setTrackingInfo(data.data);
            } else {
                setError(data.message || 'Tracking information not found');
            }
        } catch (error) {
            console.error('Error tracking order:', error);
            setError('Network error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <FontAwesomeIcon icon={faClock} className="track-status-icon track-pending" />;
            case 'shipped':
                return <FontAwesomeIcon icon={faTruck} className="track-status-icon track-shipped" />;
            case 'in_transit':
                return <FontAwesomeIcon icon={faTruck} className="track-status-icon track-in-transit" />;
            case 'out_for_delivery':
                return <FontAwesomeIcon icon={faTruck} className="track-status-icon track-out-for-delivery" />;
            case 'delivered':
                return <FontAwesomeIcon icon={faCheckCircle} className="track-status-icon track-delivered" />;
            case 'failed':
                return <FontAwesomeIcon icon={faTimesCircle} className="track-status-icon track-failed" />;
            case 'returned':
                return <FontAwesomeIcon icon={faTimesCircle} className="track-status-icon track-returned" />;
            default:
                return <FontAwesomeIcon icon={faClock} className="track-status-icon" />;
        }
    };

    const getStatusText = (status) => {
        return status.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    return (
        <div className="track-container">
            <div className="track-header">
                <h1 className="track-title">
                    <FontAwesomeIcon icon={faSearch} />
                    Track Your Order
                </h1>
                <p className="track-subtitle">Enter your tracking code to check the status of your order</p>
            </div>

            <form onSubmit={handleTrack} className="track-form">
                <div className="track-input-group">
                    <input
                        type="text"
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(e.target.value)}
                        placeholder="Enter tracking code (e.g., TRK123456789)"
                        className="track-input"
                    />
                    <button type="submit" className="track-btn" disabled={loading}>
                        {loading ? 'Tracking...' : 'Track Order'}
                    </button>
                </div>
                {error && <div className="track-error-message">{error}</div>}
            </form>

            {trackingInfo && (
                <div className="track-result">
                    <div className="track-order-summary">
                        <div className="track-summary-header">
                            <h2 className="track-order-number">Order #{trackingInfo.order_number}</h2>
                            <div className="track-order-status-badge">
                                {getStatusIcon(trackingInfo.delivery_status)}
                                <span>{getStatusText(trackingInfo.delivery_status)}</span>
                            </div>
                        </div>

                        <div className="track-summary-grid">
                            <div className="track-summary-item">
                                <FontAwesomeIcon icon={faStore} />
                                <div>
                                    <label className="track-summary-label">Shop</label>
                                    <span className="track-summary-value">{trackingInfo.shop_name || 'OurZest'}</span>
                                </div>
                            </div>
                            <div className="track-summary-item">
                                <FontAwesomeIcon icon={faUser} />
                                <div>
                                    <label className="track-summary-label">Customer</label>
                                    <span className="track-summary-value">{trackingInfo.customer_name}</span>
                                </div>
                            </div>
                            <div className="track-summary-item">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                <div>
                                    <label className="track-summary-label">Order Date</label>
                                    <span className="track-summary-value">{new Date(trackingInfo.order_date).toLocaleDateString()}</span>
                                </div>
                            </div>
                            <div className="track-summary-item">
                                <FontAwesomeIcon icon={faDollarSign} />
                                <div>
                                    <label className="track-summary-label">Total Amount</label>
                                    <span className="track-summary-value">₹{parseFloat(trackingInfo.total_amount).toFixed(2)}</span>
                                </div>
                            </div>
                            <div className="track-summary-item">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <div>
                                    <label className="track-summary-label">Payment Status</label>
                                    <span className={`track-payment-status track-${trackingInfo.payment_status}`}>
                                        {getStatusText(trackingInfo.payment_status)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="track-details">
                        <h3 className="track-details-title">Tracking Information</h3>
                        
                        <div className="track-card">
                            <div className="track-card-header">
                                <div className="track-courier-info">
                                    <FontAwesomeIcon icon={faTruck} />
                                    <span>{trackingInfo.courier_name}</span>
                                </div>
                                <div className="track-code">
                                    <label className="track-code-label">Tracking Code:</label>
                                    <span className="track-code-value">{trackingInfo.tracking_code}</span>
                                </div>
                            </div>

                            <div className="track-meta">
                                <div className="track-meta-item">
                                    <label className="track-meta-label">Receipt Number:</label>
                                    <span className="track-meta-value">{trackingInfo.courier_receipt_no}</span>
                                </div>
                                <div className="track-meta-item">
                                    <label className="track-meta-label">Shipped Date:</label>
                                    <span className="track-meta-value">{trackingInfo.shipped_date}</span>
                                </div>
                                {trackingInfo.estimated_delivery && (
                                    <div className="track-meta-item">
                                        <label className="track-meta-label">Estimated Delivery:</label>
                                        <span className="track-meta-value">{trackingInfo.estimated_delivery}</span>
                                    </div>
                                )}
                                {trackingInfo.actual_delivery && (
                                    <div className="track-meta-item">
                                        <label className="track-meta-label">Actual Delivery:</label>
                                        <span className="track-meta-value">{trackingInfo.actual_delivery}</span>
                                    </div>
                                )}
                            </div>

                            {trackingInfo.tracking_url && (
                                <a 
                                    href={trackingInfo.tracking_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="track-link"
                                >
                                    Track with Courier
                                </a>
                            )}

                            {trackingInfo.tracking_details && (
                                <div className="track-updates">
                                    <h4 className="track-updates-title">Tracking Updates</h4>
                                    <p className="track-updates-text">{trackingInfo.tracking_details}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {trackingInfo.notes && (
                        <div className="track-notes">
                            <h4 className="track-notes-title">Additional Notes</h4>
                            <p className="track-notes-text">{trackingInfo.notes}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TrackOrder;